import { asDate } from "@@/shared/date-time/as-date";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import {
    weekDayShortNameZodSchema,
    type WeekdayShortName,
} from "@@/shared/parse-weekly-schedule";
import { getWeekdayShortNameInEnglishFromDate } from "@@/utilities/date-and-time/name-of-day";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import {
    createDiscountConditionId,
    type DiscountConditionId,
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const weekdayConditionType = "WEEKDAY_CONDITION";
type WeekdayConditionType = typeof weekdayConditionType;

type WeekdayCondition = {
    _id: DiscountConditionId;
    type: WeekdayConditionType;
    weekdays: WeekdayShortName[];
};

const weekdayConditionFactory = (
    weekdays: WeekdayShortName[],
): WeekdayCondition => {
    return {
        _id: createDiscountConditionId(),
        type: "WEEKDAY_CONDITION",
        weekdays,
    };
};

const getWeekdayConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: WeekdayCondition,
    ): DiscountConditionValidator<TOrderItem> =>
    (items: TOrderItem[]) => {
        const output = items.filter(item => {
            if (item.acquireType === "BOOKABLE") {
                const weekday = getWeekdayShortNameInEnglishFromDate(
                    asDate(item.time.start),
                );
                return condition.weekdays.some(
                    day =>
                        day.toLocaleLowerCase() === weekday.toLocaleLowerCase(),
                );
            }
            return false;
        });
        return output;
    };

const weekdayConditionZodSchema: z.ZodSchema<WeekdayCondition> = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("WEEKDAY_CONDITION"),
    weekdays: z.array(weekDayShortNameZodSchema),
});

const isWeekdayCondition = (
    condition: DiscountCondition,
): condition is WeekdayCondition => {
    return condition.type === weekdayConditionType;
};

export {
    getWeekdayConditionValidator,
    isWeekdayCondition,
    weekdayConditionFactory,
    weekdayConditionType,
    weekdayConditionZodSchema,
};
export type { WeekdayCondition, WeekdayConditionType };
